import { isPrimitive } from '@sentry/core';
import { filterTelemetryValue } from './filterTelemetryValue.js';
/**
 * Sentry's `beforeSend` hook, and the only part of the boundary the SDK cannot express as
 * configuration.
 *
 * `extra` and `tags` are free-form bags that no data-collection option covers: `normalizeDepth` only
 * depth-caps `breadcrumbs.data`, `user`, `contexts` and `extra`, and never touches `tags` at all, so
 * the scalar rule has to be applied by hand. Every other channel this used to reduce — breadcrumbs,
 * `contexts`, `request` — is now closed by not registering the integration that populates it, which
 * is strictly stronger: not collected beats collected-then-stripped.
 *
 * `user` is the exception that stays. It is rebuilt rather than pruned key by key so a future SDK
 * field cannot arrive pre-approved, and it is the one control standing behind a `setUser` call site
 * that adds an email — the residual risk class this boundary explicitly cannot delegate to review.
 *
 * `exception` and its stack traces are deliberately left untouched. The message is the issue title
 * and the grouping key; redacting it would collapse unrelated failures into one unreadable issue and
 * destroy triage. Keeping error messages PHI-free therefore remains a call site obligation
 * (`.claude/skills/compliance/SKILL.md`, Rule 2) — this hook does not and cannot take it over.
 *
 * Runs last: the SDK applies scope data, then event processors, then normalization, and only then
 * calls `beforeSend`. That is why the server-side hooks can promote `ApplicationError.data` into
 * `extra` and still have it filtered — by this function, in the same callback.
 *
 * The event is copied rather than mutated, because the SDK still owns it.
 * @__NO_SIDE_EFFECTS__
 */
export function filterSentryEventData(event) {
    const filtered = { ...event };
    if (event.user) {
        filtered.user = { id: event.user.id };
    }
    if (event.extra) {
        filtered.extra = filterExtras(event.extra);
    }
    if (event.tags) {
        filtered.tags = filterTags(event.tags);
    }
    return filtered;
}
/**
 * The `extra` bag is where the server-side hooks promote `ApplicationError.data`, so it holds
 * whatever a throw site attached — including provider response objects. The shared scalar rule
 * applies unchanged: keys survive, structures collapse.
 */
function filterExtras(extras) {
    const filtered = {};
    for (const [key, value] of Object.entries(extras)) {
        filtered[key] = filterTelemetryValue(value);
    }
    return filtered;
}
/**
 * Tags are declared as primitives, but they are assembled dynamically and the declaration is not a
 * runtime guarantee, so they go through the same filter as everything else. `isPrimitive` narrows the
 * filter's `unknown` result back to the tag type, and drops the one non-primitive it can now return —
 * a `markTelemetrySafe` structure, which a tag has no room for whatever its contents.
 */
function filterTags(tags) {
    const filtered = {};
    for (const [key, value] of Object.entries(tags)) {
        const result = filterTelemetryValue(value);
        filtered[key] = isPrimitive(result) ? result : null;
    }
    return filtered;
}
//# sourceMappingURL=filterSentryEventData.js.map